import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { USER_SKILLS } from '../config';
import { SKILLS_ID } from '../NavigationBar';
import css from '../ProfileSettingsForm.css';
import sectionCss from './SkillsSection.css';
import LogoutOverlay from '../LogoutOverlay';
import { Button, IconSpinner, ToggleText } from '../../../components';
import { useIsMobile } from '../../../hooks/useIsMobile';
import ScoreBadge from '../../../components/ScoreBadge/ScoreBadge';

const SkillsSection = ({
    renderEditPencilIcon,
    handleEditSection,
    skillsGeneral,
    skillsExperience,
    displayName,
    isLoggedOut,
    isPublic,
    switchUserRoleAction,
    pageParams,
    scoreData,
    scoreReqInProgress,
}) => {
    const [isMobile] = useIsMobile();

    const primarySkillsExist = Boolean(skillsGeneral && skillsGeneral.length > 0);
    const experienceBasedSkillsExist = Boolean(skillsExperience && skillsExperience.length > 0);
    const noData = !primarySkillsExist && !experienceBasedSkillsExist;

    const showScoreData = scoreData && typeof scoreData.skillSCore === 'number';

    const scoreComponent = showScoreData && (
        <ScoreBadge scoreData={{ total: scoreData.skillSCore, maxScore: 100 }} />
    );

    const Heading = ({ children }) => (
        <h2
            className={classNames(css.infoHeading, sectionCss.infoHeading, {
                [sectionCss.infoHeadingScored]: Boolean(showScoreData),
            })}
        >
            <span>Stärken</span>
            {scoreComponent}
            {children}
        </h2>
    );

    if (isPublic && noData && !isLoggedOut) {
        return (
            <div className={css.infoBlock}>
                <Heading />
                <p className={css.noData}>{displayName} hat noch keine Stärken hinzugefügt.</p>
            </div>
        );
    }

    if (isPublic && noData) return null;

    const logoutOverlayVisible = isLoggedOut || switchUserRoleAction;

    const classes = classNames(css.infoBlock, {
        [css.noInfoBlock]: noData,
        [sectionCss.noInfoBlock]: noData,
        [css.viewProfileRequestWrapper]: logoutOverlayVisible,
        [sectionCss.viewProfileRequestWrapper]: logoutOverlayVisible,
    });

    return (
        <div className={classes} id={SKILLS_ID}>
            {scoreReqInProgress && <IconSpinner />}
            <Heading>
                {!noData && !isPublic && (
                    <div className={css.editBlock}>
                        {renderEditPencilIcon(
                            USER_SKILLS,
                            null,
                            <p className={css.editText}>
                                <FormattedMessage id="ProfileSettingsForm.editText" />
                            </p>
                        )}
                    </div>
                )}
            </Heading>
            {!noData ? (
                <ToggleText initialState={isLoggedOut} maxHeight={isMobile ? 305 : 155}>
                    <section>
                        {primarySkillsExist && (
                            <div
                                className={classNames({
                                    [sectionCss.visualSeparator]: experienceBasedSkillsExist,
                                })}
                            >
                                <p className={sectionCss.skillsSubHeading}>
                                    Das zeichnet mich aus:
                                </p>
                                <div className={sectionCss.skillsSection}>
                                    {skillsGeneral.map((skill, i) => (
                                        <div
                                            className={`${css.actionItem} ${sectionCss.skillsItem}`}
                                            key={`skill-${i}`}
                                        >
                                            {skill}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {experienceBasedSkillsExist && (
                            <div>
                                <p className={sectionCss.skillsSubHeading}>
                                    Ich habe Erfahrung mit:
                                </p>
                                <div className={sectionCss.skillsSection}>
                                    {skillsExperience.map((skill, i) => (
                                        <div
                                            className={`${css.actionItem} ${sectionCss.skillsItem}`}
                                            key={`skill-${i}`}
                                        >
                                            {skill}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                </ToggleText>
            ) : (
                <>
                    <div className={`${sectionCss.placeholderWrapper} ${css.placeholderWrapper}`}>
                        {Array.from({ length: 10 }, (_, i) => (
                            <div key={i} />
                        ))}
                    </div>

                    <Button
                        type="button"
                        onClick={() => {
                            handleEditSection(USER_SKILLS);
                        }}
                        className={classNames([
                            css.actionItem,
                            css.actionBtn,
                            sectionCss.actionBtn,
                        ])}
                    >
                        <FormattedMessage id="ProfileSettingsForm.editUserInfoAction-userSkills" />
                    </Button>
                </>
            )}
            <LogoutOverlay
                userName={displayName}
                isLoggedOut={isLoggedOut}
                rootClassName={sectionCss.viewProfileRequestAction}
                sectionName={USER_SKILLS}
                switchUserRoleAction={switchUserRoleAction}
                pageParams={pageParams}
            />
        </div>
    );
};

export default SkillsSection;
